import type React from 'react'

import { Alert } from '@/components/ui'
import type { TempUnits } from '@/insights/assets/historyGraph/types'
import type { AssetWindowState } from '@/insights/common/assetHistoryWindow'
import ReeferFuelChart from '@/insights/fuel/reeferFuelGraph/ReeferFuelChart'
import type { ReeferFuelSample } from '@/insights/fuel/reeferFuelGraph/types'
import ReeferSpeedChart from '@/insights/speed/reeferSpeedGraph/ReeferSpeedChart'
import type { ReeferSpeedSample } from '@/insights/speed/reeferSpeedGraph/types'

import { type AssetSamples, useAssetSamples } from './useAssetSamples'

const FUEL_PATH = '/insights/fuel/reefer-fuel-by-asset'
const SPEED_PATH = '/insights/speed/reefer-speed-by-asset'

interface SampleGraphProps {
  assetId: number
  companyId: number
  assetWindow: AssetWindowState
  /** The graph picker, drawn at the left of the date bar. */
  picker: React.ReactNode
}

interface SampleGraphFrameProps<T> {
  samples: AssetSamples<T>
  picker: React.ReactNode
  emptyMessage: string
  children: (data: T[]) => React.ReactNode
}

/**
 * Loading, error and empty states around a fuel or speed chart. An empty window still draws
 * the chart, with a note over it, so the date bar and picker stay usable to move on.
 */
const SampleGraphFrame = <T,>({ samples, picker, emptyMessage, children }: SampleGraphFrameProps<T>) => {
  const { data, error, isLoading, isEmpty } = samples
  return (
    <div className="relative">
      {data ? (
        children(data)
      ) : (
        <div className="flex flex-col gap-2 px-4">
          {picker}
          {error ? <Alert type="error" message={error} /> : <div className="h-[300px]" />}
        </div>
      )}
      {data && isEmpty && (
        <p className="pointer-events-none absolute inset-x-0 top-1/2 text-center text-sm text-gray-500">
          {emptyMessage}
        </p>
      )}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
        </div>
      )}
    </div>
  )
}

/** The Graphs tab's Fuel graph: reefer fuel level, as in the Reefer Fuel Graph insight. */
export const AssetFuelGraph = ({ assetId, companyId, assetWindow, picker }: SampleGraphProps) => {
  const samples = useAssetSamples<ReeferFuelSample>(FUEL_PATH, companyId, assetId, assetWindow.initial)
  const { initial } = assetWindow
  return (
    <SampleGraphFrame samples={samples} picker={picker} emptyMessage="No fuel data for this period.">
      {(data) => (
        <ReeferFuelChart
          data={data}
          from={initial.from}
          periodHours={initial.to.diff(initial.from, 'hours').hours}
          reload={samples.reload}
          navPosition="top"
          leading={picker}
          initialWindow={assetWindow.period}
          onWindowChange={assetWindow.setPeriod}
        />
      )}
    </SampleGraphFrame>
  )
}

/** The Graphs tab's Speed graph: average and maximum speed, as in the Speed Graph insight. */
export const AssetSpeedGraph = ({
  assetId,
  companyId,
  assetWindow,
  picker,
  units,
}: SampleGraphProps & { units: TempUnits }) => {
  const samples = useAssetSamples<ReeferSpeedSample>(SPEED_PATH, companyId, assetId, assetWindow.initial)
  const { initial } = assetWindow
  return (
    <SampleGraphFrame samples={samples} picker={picker} emptyMessage="No speed data for this period.">
      {(data) => (
        <ReeferSpeedChart
          data={data}
          from={initial.from}
          periodHours={initial.to.diff(initial.from, 'hours').hours}
          unit={units === 'Metric' ? 'kph' : 'mph'}
          reload={samples.reload}
          navPosition="top"
          leading={picker}
          initialWindow={assetWindow.period}
          onWindowChange={assetWindow.setPeriod}
        />
      )}
    </SampleGraphFrame>
  )
}
